import React, { Component, ErrorInfo } from 'react';

interface Props {
  children: React.ReactNode;
  /** Optional inline fallback for subtree boundaries (renders inside the page layout). */
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary — catches render crashes in child components.
 *
 * Two modes:
 * - **Top-level** (default): full-page fallback with reload button.
 *   Wrap around `<Routes>` in App.tsx to prevent white-screen-of-death.
 * - **Inline** (`inline` prop): card-style fallback that preserves the
 *   nav bar and layout. Use on high-value subtrees like ClinicalPage.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.inline) {
      return (
        <div className="m-4 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Something went wrong</h2>
          <p className="text-sm text-red-600 mb-4">
            This section encountered an error. Your data is safe.
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-4 p-8 bg-white rounded-xl shadow-lg text-center">
          <div className="text-4xl mb-4">&#9888;</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-6">
            The application encountered an unexpected error. Your data is safe — please reload to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
}
